<script lang="ts" setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { PageHero } from '~/components';
import {
  AppBreadcrumbs,
  AppButton,
  AppSkeletonLoader,
  AppTypography,
} from '~/components/base';
import { CampaignDetails } from '~/components/campaigns';
import { useCampaign } from '~/composables/useCampaign';
import { $stripHtml } from '~/utils/helpers';

definePageMeta({ layout: 'default' });

const route = useRoute();

// Captured once — pages remount per fullPath; a reactive read would flash
// the not-found state while navigating away.
const campaignId = route.params.id as string;
const {
  campaign,
  pending: campaignPending,
  error: campaignError,
  refresh: retryCampaign,
} = useCampaign(campaignId);

// Head/meta AFTER the data refs they read — placing them above caused a
// TDZ ReferenceError during client-side navigation.
useHead({ title: computed(() => campaign.value?.title ?? 'Campaign Details') });
useSeoMeta({
  description: () =>
    $stripHtml(campaign.value?.description) || 'Campaign details on VoteWise.',
  ogTitle: () => `${campaign.value?.title ?? 'Campaign Details'} | VoteWise`,
  ogDescription: () =>
    $stripHtml(campaign.value?.description) || 'Campaign details on VoteWise.',
});
const breadcrumbs = [
  { label: 'Campaigns', to: '/campaigns' },
  { label: 'Campaign Details' },
];
</script>

<template>
  <div class="campaign-detail-page">
    <PageHero constrained max-width="max-w-[640px]">
      <template v-if="campaign">
        <!-- Mobile shows a back arrow in the top nav instead (Figma). -->
        <AppBreadcrumbs
          :custom-breadcrumbs="breadcrumbs"
          :include-home="false"
          class="hidden lg:flex"
        />

        <div class="campaign-detail-page__panel">
          <CampaignDetails :campaign="campaign" />
        </div>
      </template>

      <!-- On a client-side navigation there is no server render to fall back
           on, so without this the page showed nothing at all while fetching
           (VTW-574). Matches the modal's skeleton, which renders the same
           CampaignDetails content. -->
      <div
        v-else-if="campaignPending"
        data-testid="campaign-loading"
        class="campaign-detail-page__panel flex flex-col gap-3"
      >
        <AppSkeletonLoader :items="[{ height: '32px' }]" container-class="" />
        <AppSkeletonLoader :items="[{ height: '160px' }]" container-class="" />
      </div>

      <!-- A failed request and a missing campaign are different things: the
           first is worth retrying, the second isn't. `find()` maps a 404 to
           `null`, so only a real failure (5xx/network) reaches `error`.
           Checked before the not-found fallback, and matching the copy the
           modal already uses — which links here as its own retry, so this
           page must not relabel the same failure as "doesn't exist". -->
      <div
        v-else-if="campaignError"
        data-testid="campaign-error"
        class="campaign-detail-page__not-found flex flex-col items-center gap-3 text-center"
      >
        <AppTypography type="title-5">
          Couldn&rsquo;t load this campaign
        </AppTypography>
        <AppButton
          label="Try again"
          data-testid="campaign-retry"
          @click="retryCampaign()"
        />
      </div>

      <div
        v-else
        data-testid="campaign-not-found"
        class="campaign-detail-page__not-found flex flex-col items-center gap-3 text-center"
      >
        <AppTypography type="title-5">Campaign not found</AppTypography>
        <NuxtLink
          to="/campaigns"
          class="campaign-detail-page__back no-underline"
        >
          <Icon name="solar:arrow-left-linear" class="shrink-0" />
          <AppTypography type="body-bold" element="span"
            >Back to Campaigns</AppTypography
          >
        </NuxtLink>
      </div>
    </PageHero>
  </div>
</template>

<style scoped lang="scss">
.campaign-detail-page {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-height: calc(100vh - var(--header-height));

  &__panel {
    padding: $spacing-2xl;
    border-radius: $radius-2xl;
    background-color: $surface-color-bg;
  }

  &__not-found {
    padding: $spacing-6xl $spacing-lg;
  }

  &__back {
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    color: $text-color-brand;

    &.router-link-active {
      background-color: transparent;
    }
  }
}
</style>
